import React from 'react';
import { Check, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import CostCalculator from '../Home/components/CostCalculator';

const PricingTier = ({ name, price, description, features, highlighted = false, customPrice = false }) => {
  const handleGetStarted = () => {
    if (customPrice) {
      window.location.href = 'mailto:marcus@deltaops.app';
    } else {
      // Add UTM parameters for analytics
      const params = new URLSearchParams({
        plan: name.toLowerCase(),
        utm_source: 'pricing_page',
        utm_medium: 'website',
        utm_campaign: 'direct_signup'
      });
      window.location.href = `https://console.deltaops.app/signup?${params.toString()}`;
    }
  };

  return (
    <div className={`p-8 rounded-xl ${highlighted ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'} border`}>
      <h3 className="text-xl font-semibold text-gray-900">{name}</h3>
      <div className="mt-4 flex items-baseline">
        {!customPrice ? (
          <>
            <span className="text-4xl font-bold text-gray-900">${price}</span>
            <span className="ml-1 text-gray-500">/month</span>
          </>
        ) : (
          <span className="text-2xl font-bold text-gray-900">Contact Us</span>
        )}
      </div>
      <p className="mt-2 text-gray-600">{description}</p>
      <ul className="mt-6 space-y-4">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            {feature.comingSoon ? (
              <Clock className="h-5 w-5 text-blue-500 shrink-0" />
            ) : (
              <Check className="h-5 w-5 text-green-500 shrink-0" />
            )}
            <span className="ml-3 text-gray-600">
              {feature.text}
              {feature.comingSoon && (
                <span className="ml-2 text-sm text-blue-600">(Coming Soon)</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {customPrice ? (
        <Button 
          className="w-full mt-8 bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
          variant="outline"
          onClick={handleGetStarted}
        >
          Contact Sales
        </Button>
      ) : (
        <Button 
          className={`w-full mt-8 ${highlighted ? '' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`}
          variant={highlighted ? 'default' : 'outline'}
          onClick={handleGetStarted}
        >
          Get Started
        </Button>
      )}
    </div>
  );
};

const Pricing = () => {
  const tiers = [
    {
      name: 'Free',
      price: '0',
      description: 'Perfect for small teams getting started',
      features: [
        { text: 'Track up to 1,000 changes per month' },
        { text: 'Basic search functionality' },
        { text: 'Git integration' },
        { text: 'Community support' }
      ]
    },
    {
      name: 'Pro',
      price: '79',
      description: 'For teams that need more power and control',
      features: [
        { text: 'Unlimited changes' },
        { text: 'Advanced semantic search' },
        { text: 'All integrations (Git, CI/CD, Cloud)' },
        { text: 'Priority email support' },
        { text: 'Custom change templates' }
      ],
      highlighted: true
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      features: [
        { text: 'Everything in Pro' },
        { text: 'Custom integrations' },
        { text: 'Advanced analytics' },
        { text: 'SSO / SAML' },
        { text: 'Dedicated support' },
        { text: 'SLA guarantees' }
      ],
      customPrice: true
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h1>
        <p className="mt-4 text-xl text-gray-600">
          Choose the plan that best fits your team's needs
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {tiers.map((tier) => (
          <PricingTier key={tier.name} {...tier} />
        ))}
      </div>
      <div className="mt-8 text-center bg-blue-50 rounded-lg p-6">
        <p className="text-blue-700">
          Already have an account?{' '}
          <button 
            onClick={() => window.location.href = 'https://console.deltaops.app/login'}
            className="text-blue-600 font-medium hover:underline"
          >
            Sign in to your dashboard
          </button>
        </p>
      </div>
      {/* Calculator Section */}
      <div className="py-12 border-b border-gray-200">
        <CostCalculator />
      </div>
      <div className="mt-12 text-center">
        <p className="text-sm text-gray-500">
          Have questions? <a href="mailto:marcus@deltaops.app" className="text-blue-600 hover:text-blue-800">Contact us</a>
        </p>
      </div>
    </div>
  );
};

export default Pricing;